/* eslint-disable @typescript-eslint/naming-convention */
import React from "react";
import ky from "ky";
import { datadogLogs } from "@datadog/browser-logs";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  ApiError,
  getStandardHeaders,
  parseError,
  SimpleApiResult,
} from "../../apiUtils";
import { Tier } from "../../domain/billing";
import { AccountBase } from "../../domain/people";
import snakeCase from "lodash/snakeCase";
import { ProgramChoicesEnum } from "../../api-client";

type BillingEventServer = {
  id: number;
  program: number;
  account: string;
  kind: string;
  amount_credits: number | null;
  current_credits: number;
  coiled_credit_value: number;
  credit_expiration: string | null;
  event_datetime: string;
  event_added_datetime: string;
  creator: string | null;
  user: string | null;
  instance: number | null;
  cluster: number | null;
  metadata: Record<string, any>;
  uptime: number | null;
  note: string;
};

type BillingEventPaginatedResponse = {
  // drf paginator returns more values than are listed here
  count: number;
  page_size: number;
  results: BillingEventServer[];
};

export type BillingEvent = {
  id: number;
  program: number;
  accountSlug: string;
  kind: string;
  amountCredits: number | null;
  currentCredits: number;
  coiledCreditValue: number;
  creditExpiration?: string;
  eventDatetime: string;
  eventAddedDatetime: string;
  creator?: string;
  user?: string;
  cluster?: number;
  instance?: number;
  note: string;
  uptime?: number;
  metadata: string;
};

export type BillingColumn = keyof BillingEvent;

export const BILLING_COLUMN_KEY = "coiled_billing_columns";
export const ALL_COLUMNS: readonly BillingColumn[] = [
  "id",
  "program",
  "accountSlug",
  "kind",
  "amountCredits",
  "currentCredits",
  "note",
  "eventDatetime",
  "eventAddedDatetime",
  "creator",
  "user",
  "cluster",
  "instance",
  "uptime",
  "coiledCreditValue",
  "creditExpiration",
  "metadata",
];

export const DEFAULT_COLUMNS = ALL_COLUMNS;

type LoadBillingEventsResult =
  | { type: "success"; data: BillingEvent[]; count: number; pageSize: number }
  | { type: "error"; error: ApiError };

export const queryStringToBillingSearchParams = (
  s: string,
): BillingEventSearchParams => {
  if (!s) {
    return {};
  }
  const p = new URLSearchParams(s);
  try {
    const end_time = p.get("end_time");
    const start_time = p.get("start_time");
    const page = p.get("page");
    const account_slug = p.get("account_slug");
    const bp: BillingEventSearchParams = {};
    if (end_time) {
      bp.end_time = new Date(end_time);
    }
    if (start_time) {
      bp.start_time = new Date(start_time);
    }
    if (page) {
      bp.page = parseInt(page, 10);
    }
    if (account_slug) {
      bp.account_slug = account_slug;
    }
    return bp;
  } catch (error) {
    datadogLogs.logger.warn("Couldn't parse billing searchParams", { error });
    return {};
  }
};

const pruneSearchParams = <ParamsT extends Record<string, any>>(
  p: ParamsT,
): ParamsT =>
  // Remove properties that have a value of undefined or black string.
  // Useful for creating a more human-readable query string.
  Object.entries(p).reduce((out, [k, v]) => {
    if (v !== "" && typeof v != "undefined") {
      return { ...out, [k]: v };
    }
    return out;
  }, {} as ParamsT);

export const billingSearchParamsToQueryString = (
  p: BillingEventSearchParams,
): string => {
  const { start_time, end_time, page, ...rest } = pruneSearchParams(p);
  // Can't easily express that pruneSearchParams returns an object
  // with all properties that have undefined values removed, so
  // casting.
  const usp = new URLSearchParams(rest as Record<string, string>);
  if (start_time) {
    usp.set("start_time", start_time.toUTCString());
  }
  if (end_time) {
    usp.set("end_time", end_time.toUTCString());
  }
  if (page) {
    usp.set("page", page.toString());
  }
  return usp.toString();
};

export const getBillingEvents = async (
  searchParams: BillingEventSearchParams,
): Promise<LoadBillingEventsResult> => {
  const searchParamsCopy = { ...searchParams };
  if (!searchParamsCopy.page) {
    searchParamsCopy.page = 1;
  } else {
    // DRF has 1-indexed pagination, but MUI element is 0-indexed
    searchParamsCopy.page += 1;
  }
  const urlSearchParams = billingSearchParamsToQueryString(searchParamsCopy);
  try {
    const results = (await ky
      .get(`/api/v1/staff/billing-events?${urlSearchParams}`, {
        headers: getStandardHeaders(),
      })
      .json()) as BillingEventPaginatedResponse;

    return {
      type: "success",
      count: results.count,
      pageSize: results.page_size,
      data: results.results.map(
        ({
          id,
          program,
          account: accountSlug,
          kind,
          amount_credits: amountCredits,
          current_credits: currentCredits,
          coiled_credit_value: coiledCreditValue,
          credit_expiration: creditExpriration,
          event_datetime: eventDatetime,
          event_added_datetime: eventAddedDatetime,
          creator,
          user,
          instance,
          cluster,
          note,
          uptime,
          metadata: metadataRaw,
        }) => {
          let metadata: string;
          try {
            metadata = JSON.stringify(metadataRaw, null, 2);
          } catch (error) {
            metadata = "(error parsing metadata)";
          }
          return {
            id,
            program,
            accountSlug,
            kind,
            amountCredits: amountCredits ? amountCredits : null,
            currentCredits,
            coiledCreditValue,
            creditExpiration: creditExpriration ? creditExpriration : undefined,
            eventDatetime,
            eventAddedDatetime,
            creator: creator ? creator : undefined,
            user: user ? user : undefined,
            instance: instance ? instance : undefined,
            cluster: cluster ? cluster : undefined,
            note,
            uptime: uptime ? uptime : undefined,
            metadata,
          };
        },
      ),
    };
  } catch (err) {
    return {
      type: "error",
      error: parseError(err),
    };
  }
};

export type BillingEventSearchParams = {
  account_slug?: string;
  start_time?: Date;
  end_time?: Date;
  page?: number;
};

export const useTransformedSearchParams = <T>(
  toQueryString: (params: T) => string,
  fromQueryString: (queryString: string) => T,
): [T, (params: T) => void] => {
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [params, innerSetParams] = React.useState(
    fromQueryString(location.search),
  );
  const setParams = (p: T): void => {
    const search = toQueryString(p);
    setSearchParams(search);
  };

  React.useEffect(() => {
    innerSetParams(fromQueryString(location.search));
  }, [location.search, location.pathname, fromQueryString]);

  return [params, setParams];
};

export type AccountSearchParams = {
  account_slug?: string;
  page?: number;
};

type LoadAccountsResult =
  | { type: "success"; data: AccountBase[]; count: number; pageSize: number }
  | { type: "error"; error: ApiError };

type RawAccountBase = Omit<
  AccountBase,
  | "coreLimit"
  | "creditLimit"
  | "membersLimit"
  | "hasQuota"
  | "avatarUurl"
  | "earlyAdopter"
> & {
  user_limit: number;
  core_limit: number;
  credit_limit: number;
  has_quota: boolean;
  avatar_url?: string;
  early_adopter: boolean;
};

type RawAccountsResponse = {
  count: number;
  page_size: number;
  results: RawAccountBase[];
};

const mungeAccountBase = ({
  id,
  slug,
  avatar_url,
  name,
  core_limit,
  credit_limit,
  early_adopter,
  active,
  user_limit,
}: RawAccountBase): AccountBase => ({
  id,
  slug,
  avatarUrl: avatar_url,
  name,
  coreLimit: core_limit,
  creditLimit: credit_limit,
  earlyAdopter: early_adopter,
  active,
  membersLimit: user_limit,
});

export const accountParamsToQueryString = (
  params: AccountSearchParams,
): string => {
  const { page, ...rest } = pruneSearchParams(params);
  const usp = new URLSearchParams(rest as Record<string, string>);
  if (page) {
    usp.set("page", page.toString());
  }
  return usp.toString();
};

export const queryStringToAccountParams = (s: string): AccountSearchParams => {
  if (!s) {
    return {};
  }
  const p = new URLSearchParams(s);
  try {
    const account_slug = p.get("account_slug");
    const page = p.get("page");
    const params: AccountSearchParams = {};
    if (account_slug) {
      params.account_slug = account_slug;
    }
    if (page) {
      params.page = parseInt(page, 10);
    }
    return params;
  } catch (error) {
    datadogLogs.logger.warn("Couldn't parse account search params", { error });
    return {};
  }
};

export const getAccounts = async (
  params: AccountSearchParams,
): Promise<LoadAccountsResult> => {
  try {
    const searchParamsCopy = { ...params };
    if (!searchParamsCopy.page) {
      searchParamsCopy.page = 1;
    } else {
      // DRF has 1-indexed pagination, but MUI element is 0-indexed
      searchParamsCopy.page += 1;
    }
    const urlSearchParams = accountParamsToQueryString(searchParamsCopy);
    const result = (await ky
      .get(`/api/v1/staff/accounts?${urlSearchParams}`, {
        headers: getStandardHeaders(),
      })
      .json()) as RawAccountsResponse;
    return {
      type: "success",
      count: result.count,
      pageSize: result.page_size,
      data: result.results.map(mungeAccountBase),
    };
  } catch (err) {
    return { type: "error", error: parseError(err) };
  }
};

export const activateOrDeactivateAccount = async (
  active: boolean,
  accountSlug: string,
): Promise<SimpleApiResult> => {
  try {
    await ky
      .patch(`/api/v1/staff/accounts/${accountSlug}/activate/`, {
        headers: getStandardHeaders(),
        json: {
          active,
        },
      })
      .json();
    return {
      type: "success",
    };
  } catch (err) {
    return { type: "error", error: parseError(err) };
  }
};

export const upgradeToEnterprise = async (
  accountSlug: string,
  programLimits: Omit<ProgramLimits, "creditAllotment">,
): Promise<SimpleApiResult> => {
  try {
    await ky.patch(`/api/v1/staff/programs/${accountSlug}/upgrade/`, {
      headers: getStandardHeaders(),
      json: {
        core_limit: programLimits.coreLimit,
        node_limit: programLimits.nodeLimit,
        members_limit: programLimits.membersLimit,
        core_limit_members: programLimits.coreLimitMembers,
        gpus_limit: programLimits.gpusLimit,
        coiled_credit_value: programLimits.coiledCreditValue,
      },
    });
    return { type: "success" };
  } catch (err) {
    return { type: "error", error: parseError(err) };
  }
};

export const downgradeToFree = async (
  accountSlug: string,
  programLimits: ProgramLimits,
): Promise<SimpleApiResult> => {
  try {
    await ky.patch(`/api/v1/staff/programs/${accountSlug}/downgrade/`, {
      headers: getStandardHeaders(),
      json: {
        core_limit: programLimits.coreLimit,
        node_limit: programLimits.nodeLimit,
        members_limit: programLimits.membersLimit,
        core_limit_members: programLimits.coreLimitMembers,
        gpus_limit: programLimits.gpusLimit,
        coiled_credit_value: programLimits.coiledCreditValue,
        credit_allotment: programLimits.creditAllotment,
      },
    });
    return { type: "success" };
  } catch (err) {
    return { type: "error", error: parseError(err) };
  }
};

type RawProgramLimits = {
  core_limit: number;
  node_limit: number;
  members_limit: number;
  core_limit_members: number;
  gpus_limit: number;
  coiled_credit_value: number;
  credit_allotment: number;
};

export type ProgramLimits = {
  coreLimit: number;
  nodeLimit: number;
  membersLimit: number;
  coreLimitMembers: number;
  gpusLimit: number;
  coiledCreditValue: number;
  creditAllotment: number;
};

const mungeLimits = ({
  core_limit: coreLimit,
  node_limit: nodeLimit,
  members_limit: membersLimit,
  core_limit_members: coreLimitMembers,
  gpus_limit: gpusLimit,
  coiled_credit_value: coiledCreditValue,
  credit_allotment: creditAllotment,
}: RawProgramLimits): ProgramLimits => ({
  coreLimit,
  nodeLimit,
  membersLimit,
  coreLimitMembers,
  gpusLimit,
  coiledCreditValue,
  creditAllotment,
});

export const updateLimits = async (
  accountSlug: string,
  limits: ProgramLimits,
  currentLimits: ProgramLimits,
): Promise<SimpleApiResult> => {
  try {
    const noDupes: Partial<ProgramLimits> = Object.entries(limits).reduce(
      (out, [k, v]) => {
        if (
          typeof v !== "undefined" &&
          currentLimits[k as keyof ProgramLimits].toString() !== v.toString()
        ) {
          return {
            ...out,
            [k]: v,
          };
        } else {
          return out;
        }
      },
      {} as Partial<ProgramLimits>,
    );
    await ky.patch(`/api/v1/staff/programs/${accountSlug}/limits/`, {
      headers: getStandardHeaders(),
      json: Object.entries(noDupes).reduce(
        (out, [k, v]) => ({
          ...out,
          [snakeCase(k)]: v,
        }),
        {} as Record<string, any>,
      ),
    });
    return { type: "success" };
  } catch (error) {
    return { type: "error", error: String(error) };
  }
};

export type DefaultLimits = {
  [ProgramChoicesEnum.Free]: ProgramLimits;
  [ProgramChoicesEnum.Payg]: ProgramLimits;
  [ProgramChoicesEnum.Enterprise]: ProgramLimits;
};

type DefaultLimitsResult =
  | { type: "success"; data: DefaultLimits }
  | { type: "error"; error: ApiError | string };

type RawProgramTiersResponse = {
  results: (RawProgramLimits & { name: Tier })[];
};

export const getDefaultLimits = async (): Promise<DefaultLimitsResult> => {
  try {
    const results = (
      (await ky
        .get(`/api/v1/staff/programs/tiers/`, {
          headers: getStandardHeaders(),
        })
        .json()) as RawProgramTiersResponse
    ).results;
    const free = results.find(({ name }) => name === Tier.Free);
    const payg = results.find(({ name }) => name === Tier.Payg);
    const enterprise = results.find(({ name }) => name === Tier.Enterprise);
    if (!free || !payg || !enterprise) {
      return { type: "error", error: "Error parsing ProgramTiers" };
    }
    return {
      type: "success",
      data: {
        [Tier.Free]: mungeLimits(free),
        [Tier.Payg]: mungeLimits(payg),
        [Tier.Enterprise]: mungeLimits(enterprise),
      },
    };
  } catch (error) {
    // keepin it sloppy for now, this is staff-facing
    return { type: "error", error: String(error) };
  }
};
